/**
 * StepFlowManager - Handles step navigation and auto-advance logic
 */
export class StepFlowManager {
  constructor(
    stepProcessor,
    promptResolver,
    sessionCoordinator,
    redirectHandler,
  ) {
    this.stepProcessor = stepProcessor;
    this.promptResolver = promptResolver;
    this.sessionCoordinator = sessionCoordinator;
    this.redirectHandler = redirectHandler;
  }

  /**
   * Process current step (prompt or auto-advance)
   */
  async processCurrentStep(introMessage = "") {
    const sessionInfo = this.sessionCoordinator.getSessionInfo();
    const { currentStep, sessionData } = sessionInfo;

    // Dynamic skip: if skipIf returns true, skip this step entirely
    if (currentStep.skipIf && typeof currentStep.skipIf === "function") {
      const shouldSkip = await currentStep.skipIf(sessionData);
      if (shouldSkip) {
        this.sessionCoordinator.moveToNextStep();
        const nextInfo = this.sessionCoordinator.getSessionInfo();
        if (!nextInfo.hasMoreSteps && !nextInfo.currentStep) {
          return this._handleCompletionFromSkip(sessionData, introMessage);
        }
        return await this.processCurrentStep(introMessage);
      }
    }

    // Handle auto-advance steps (no prompt)
    if (!currentStep.prompt) {
      return await this.handleAutoAdvanceStep(
        currentStep,
        sessionData,
        introMessage,
      );
    }

    // Handle regular steps with prompts
    const promptResult = await this.stepProcessor.processPromptStep(
      currentStep,
      sessionData,
      introMessage,
    );

    // Persist modified sessionData back to SessionManager after prompt step processing
    this.sessionCoordinator.sessionManager.mergeSessionData(sessionData);

    return promptResult;
  }

  /**
   * Handle auto-advance step logic
   */
  async handleAutoAdvanceStep(step, sessionData, introMessage = "") {
    const callbacks = this.sessionCoordinator.createCallbackTriggers(
      this.sessionCoordinator.sessionManager.currentCommandName,
      sessionData,
    );

    // Process the auto-advance step
    const stepResult = await this.stepProcessor.processAutoAdvanceStep(
      step,
      sessionData,
      introMessage,
      callbacks,
    );

    if (stepResult.error) {
      return { type: "error", result: stepResult };
    }

    // Persist modified sessionData back to SessionManager after step processing
    this.sessionCoordinator.sessionManager.mergeSessionData(sessionData);

    // Move to next step first to determine if this was the last step
    this.sessionCoordinator.moveToNextStep();

    // Get updated session info after moving
    const sessionInfo = this.sessionCoordinator.getSessionInfo();

    // Check for mid-step redirect ONLY if there are more steps
    // If this was the last step, we'll handle the redirect in completion logic
    if (this.redirectHandler && step.redirect && sessionInfo.hasMoreSteps) {
      // debug: auto-advance step has redirect property (mid-step)
      const midRedirect = await this.redirectHandler.handleMidStepRedirect(
        step,
        null,
        sessionData,
        stepResult.output || "",
        callbacks,
      );

      // debug: midRedirect result

      if (midRedirect) {
        if (midRedirect.startCommand) {
          return {
            output: stepResult.output || "",
            nextCommand: midRedirect.startCommand.target,
            nextCommandData: midRedirect.startCommand.data,
          };
        }

        if (midRedirect.nextCommand) {
          return {
            output: stepResult.output || "",
            nextCommand: midRedirect.nextCommand,
            nextCommandData: midRedirect.nextCommandData,
          };
        }
      } else {
        // debug: No redirect returned from handleMidStepRedirect
      }
    } else {
      // debug: No redirect handler, no redirect property, or last step (will handle in completion)
    }

    // Determine next action (with redirect support for auto-advance steps)
    // sessionInfo already fetched above after moveToNextStep

    console.log("🔍 StepFlowManager - after moveToNextStep:", {
      hasMoreSteps: sessionInfo.hasMoreSteps,
      currentStepIndex: sessionInfo.currentStepIndex,
      totalSteps: sessionInfo.totalSteps,
    });

    // If no more steps, treat this as completion and run redirect/completion pipeline
    if (!sessionInfo.hasMoreSteps) {
      console.log("🎯 StepFlowManager - No more steps, handling completion...");
      // Attach command reference for completion processing (mirrors CommandFlowOrchestrator)
      if (this.sessionCoordinator.sessionManager?.session?.command) {
        sessionData._command =
          this.sessionCoordinator.sessionManager.session.command;
      }

      console.log("🔍 Checking redirectHandler:", {
        hasRedirectHandler: !!this.redirectHandler,
        stepOutput: stepResult.output,
        stepHasRedirect: !!step.redirect,
        stepIndex: sessionInfo.currentStepIndex - 1, // -1 because we already moved to next
      });

      if (this.redirectHandler) {
        console.log(
          "✅ RedirectHandler exists, calling handleCompletion with step:",
          {
            hasRedirect: !!step.redirect,
            redirectFunction: step.redirect?.toString().substring(0, 50),
          },
        );
        const completionResult = await this.redirectHandler.handleCompletion(
          step,
          sessionData,
          null,
          stepResult.output || "",
          callbacks,
        );

        console.log(
          "🏁 StepFlowManager - auto-advance completion result:",
          completionResult,
        );

        // Directly return redirect/nextCommand shapes expected by UI handlers
        if (completionResult.startCommand) {
          return {
            nextCommand: completionResult.startCommand.target,
            nextCommandData: completionResult.startCommand.data,
          };
        }

        if (completionResult.nextCommand) {
          return completionResult; // { output?, nextCommand, nextCommandData }
        }

        console.log("✨ Returning completion output:", completionResult.output);
        return { output: completionResult.output || stepResult.output || null };
      }

      // Fallback: original completion shape
      return { output: stepResult.output || null };
    }

    // Continue immediately to next step (auto-advance chain) or return prompt result handled by handleNextStep
    return await this.handleNextStep(stepResult.output || "");
  }

  /**
   * Handle next step navigation
   */
  async handleNextStep(stepOutput) {
    const sessionInfo = this.sessionCoordinator.getSessionInfo();
    const { currentStep: nextStep, sessionData, hasMoreSteps } = sessionInfo;

    // No next step: trigger completion pipeline (onComplete)
    if (!nextStep) {
      return this._handleCompletionFromSkip(sessionData, stepOutput);
    }

    // Dynamic skip: if skipIf returns true, skip this step entirely
    if (nextStep.skipIf && typeof nextStep.skipIf === "function") {
      const shouldSkip = await nextStep.skipIf(sessionData);
      if (shouldSkip) {
        this.sessionCoordinator.moveToNextStep();
        return await this.handleNextStep(stepOutput);
      }
    }

    // Handle auto-advance next step
    if (!nextStep.prompt) {
      return await this.handleAutoAdvanceStep(
        nextStep,
        sessionData,
        stepOutput,
      );
    }

    // Handle regular next step with prompt - use processPromptStep to handle load function
    const stepResult = await this.stepProcessor.processPromptStep(
      nextStep,
      sessionData,
      stepOutput,
    );

    if (stepResult.error) {
      return {
        type: "error",
        result: stepResult,
      };
    }

    return { type: "prompt", prompt: stepResult.prompt };
  }

  /**
   * Handle completion when the last step(s) were skipped via skipIf.
   * Mirrors the completion logic in handleAutoAdvanceStep.
   */
  async _handleCompletionFromSkip(sessionData, stepOutput) {
    if (this.sessionCoordinator.sessionManager?.session?.command) {
      sessionData._command =
        this.sessionCoordinator.sessionManager.session.command;
    }

    if (this.redirectHandler) {
      const callbacks = this.sessionCoordinator.createCallbackTriggers(
        this.sessionCoordinator.sessionManager.currentCommandName,
        sessionData,
      );
      const completionResult = await this.redirectHandler.handleCompletion(
        {}, // no specific step — last step was skipped
        sessionData,
        null,
        stepOutput || "",
        callbacks,
      );

      if (completionResult.startCommand) {
        return {
          nextCommand: completionResult.startCommand.target,
          nextCommandData: completionResult.startCommand.data,
        };
      }
      if (completionResult.nextCommand) {
        return completionResult;
      }
      return { output: completionResult.output || stepOutput || null };
    }

    return { output: stepOutput || null };
  }

  /**
   * Build intro message for command
   */
  async buildIntroMessage(command, sessionData, callbacks, promptResolver) {
    return await promptResolver.buildIntroMessage(
      command,
      sessionData,
      callbacks,
    );
  }
}
